import React from 'react';

/**
 * Modern people tile grid for committee / past presidents / staff sections.
 * Each tile is a square portrait inside a gradient-border card with a
 * floating glass info strip at the bottom that lifts on hover.
 */
export const PeopleGrid = ({ people = [], variant = 'committee' }) => {
  if (!Array.isArray(people) || people.length === 0) return null;

  const cols =
    variant === 'past_presidents'
      ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
      : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4';

  return (
    <div
      className={`grid ${cols} gap-5 md:gap-6 mt-6`}
      data-testid={`people-grid-${variant}`}
    >
      {people.map((p) => (
        <div
          key={p.id || p.name}
          className="group relative"
          data-testid="people-grid-item"
        >
          {/* Gradient frame — uses a 1px gradient border via padding */}
          <div className="relative rounded-3xl bg-gradient-to-br from-[#D4AF37] via-[#1565C0]/40 to-[#0A1628] p-[2px] shadow-md group-hover:shadow-2xl transition-shadow duration-300">
            <div className="relative rounded-[1.4rem] overflow-hidden bg-white">
              {/* Original square tile — responsive via aspect-square */}
              <div
                className="aspect-square w-full bg-gradient-to-br from-[#1976D2] via-[#1565C0] to-[#0D47A1] transition-transform duration-500 group-hover:scale-[1.03]"
                style={
                  p.image
                    ? {
                        backgroundImage: `url(${p.image})`,
                        backgroundSize: p.image_size || 'contain',
                        backgroundPosition: p.image_pos || 'center bottom',
                        backgroundRepeat: 'no-repeat',
                      }
                    : undefined
                }
              >
                {!p.image && (
                  <div className="w-full h-full flex items-center justify-center text-white text-4xl font-black">
                    {(p.name || '?').trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Bottom name/role card — integrated, not floating */}
              <div className="relative bg-white px-3 py-3 border-t border-gray-100">
                <div className="font-bold text-sm md:text-[15px] text-[#0A1628] leading-tight break-words group-hover:text-[#1565C0] transition-colors">
                  {p.name}
                </div>
                {p.role && (
                  <div className="mt-1 flex items-start gap-1.5">
                    <span className="block w-4 h-px bg-[#D4AF37] mt-2 shrink-0"></span>
                    <span className="text-[10px] md:text-[11px] text-[#1565C0] font-black uppercase tracking-[0.15em] break-words leading-tight">
                      {p.role}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hover glow */}
          <div className="pointer-events-none absolute -inset-2 -z-10 rounded-3xl bg-gradient-to-br from-[#D4AF37]/0 via-[#1565C0]/0 to-[#0A1628]/0 group-hover:from-[#D4AF37]/30 group-hover:via-[#1565C0]/20 group-hover:to-[#0A1628]/0 blur-2xl transition-all duration-500" />
        </div>
      ))}
    </div>
  );
};

export default PeopleGrid;
